import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  DatePicker,
  Image,
  Input,
  message,
  Pagination,
  Popconfirm,
  Radio,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CloseCircleOutlined,
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { BiEdit } from "react-icons/bi";
import { IoCopyOutline, IoTrashOutline } from "react-icons/io5";
import { IoMdOpen } from "react-icons/io";
import { FaRegEye } from "react-icons/fa";
import { LuUndo2 } from "react-icons/lu";
import {
  TbSquareNumber1,
  TbSquareNumber2,
  TbSquareNumber3,
} from "react-icons/tb";
import moment from "moment";
import dayjs from "dayjs";
import axios from "../../config/axiosInstance";
import LinearProgress from "@mui/material/LinearProgress";

import { API } from "../../config/API";
import { s3FallbackUrl } from "../../utils/mediaFallback";
import { POST, PUT } from "../../utils/apirequest";
import { LoadCategories } from "../../utils/commonapis";

import "./styles.css";
import {
  ContentType,
  CONTENT_TYPE_LABELS,
} from "./component/fieldConfig";
import { API_ENDPOINTS, URL_PATTERNS } from "./component/apiMapper";
import NewsLog from "../../component/newsLog";

const ALL_CONTENT_TYPES: ContentType[] = [
  "news",
  "article",
  "editorial",
  "vidya",
  "njayar",
];

// =================== URL Param Helpers ===================

function readUrlParams(search: string) {
  const q = new URLSearchParams(search);
  return {
    tab: (q.get("tab") as ContentType) || "news",
    page: Number(q.get("page")) || 1,
    limit: Number(q.get("limit")) || 10,
    search: q.get("search") || "",
    userId: q.get("userId") || "",
    createdAt: q.get("createdAt") || "",
    catId: q.get("catId") || "",
    isHighPriority: q.get("isHighPriority") || "",
    type: q.get("type") || "",
    globalSearch: q.get("globalSearch") || "",
  };
}

function buildUrlString(params: Record<string, string | number>) {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined) {
      p.set(k, String(v));
    }
  });
  return p.toString();
}

// =================== Component ===================

function UnifiedContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const User = useSelector((state: any) => state.User.user);
  const urlParams = readUrlParams(location.search);

  const [contentType, setContentType] = useState<ContentType>(urlParams.tab);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [indicator, setIndicator] = useState(true);

  const [totalCount, setTotalCount] = useState(0);
  const [publishedCount, setPublishedCount] = useState(0);
  const [trashedCount, setTrashedCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  const [scheduledCount, setScheduledCount] = useState(0);

  const [page, setPage] = useState(urlParams.page);
  const [pageSize, setPageSize] = useState(urlParams.limit);

  const [filterSearch, setFilterSearch] = useState(urlParams.search);
  const [filterUserId, setFilterUserId] = useState(urlParams.userId);
  const [filterCreatedAt, setFilterCreatedAt] = useState(urlParams.createdAt);
  const [filterCatId, setFilterCatId] = useState(urlParams.catId);
  const [filterPriority, setFilterPriority] = useState(urlParams.isHighPriority);
  const [filterStatus, setFilterStatus] = useState(urlParams.type);

  const [globalSearch, setGlobalSearch] = useState(urlParams.globalSearch);
  const [globalSearchResults, setGlobalSearchResults] = useState<
    Record<ContentType, { data: any[]; totalCount: number }>
  >({
    news: { data: [], totalCount: 0 },
    article: { data: [], totalCount: 0 },
    editorial: { data: [], totalCount: 0 },
    vidya: { data: [], totalCount: 0 },
    njayar: { data: [], totalCount: 0 },
  });
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);

  const [usersList, setUsersList] = useState<any[]>([]);
  const [categoryList, setCategoryList] = useState<any[]>([]);

  const [logShow, setLogShow] = useState(false);
  const [logItemId, setLogItemId] = useState<any>(null);

  const globalSearchTimeout = useRef<any>(null);

  // =================== URL Sync ===================

  const currentUrlState = useCallback(() => {
    const params: Record<string, string | number> = { tab: contentType };
    if (page !== 1) params.page = page;
    if (pageSize !== 10) params.limit = pageSize;
    if (filterSearch) params.search = filterSearch;
    if (filterUserId) params.userId = filterUserId;
    if (filterCreatedAt) params.createdAt = filterCreatedAt;
    if (filterCatId) params.catId = filterCatId;
    if (filterPriority) params.isHighPriority = filterPriority;
    if (filterStatus) params.type = filterStatus;
    if (globalSearch) params.globalSearch = globalSearch;
    return params;
  }, [contentType, page, pageSize, filterSearch, filterUserId, filterCreatedAt, filterCatId, filterPriority, filterStatus, globalSearch]);

  const syncUrl = useCallback(
    (overrides: Record<string, string | number> = {}) => {
      const params = { ...currentUrlState(), ...overrides };
      Object.keys(params).forEach((k) => {
        if (params[k] === "" || params[k] === null || params[k] === undefined) delete params[k];
      });
      navigate({ search: buildUrlString(params) }, { replace: true });
    },
    [currentUrlState, navigate]
  );

  // =================== Data Loading ===================

  const loadData = async (overridePage?: number, overridePageSize?: number) => {
    const pg = overridePage ?? page;
    const ps = overridePageSize ?? pageSize;
    setLoading(true);
    setIndicator(true);
    const endpoints = API_ENDPOINTS[contentType];
    const filterPayload: any = { start: (pg - 1) * ps, limit: ps };
    if (filterSearch) filterPayload.search = filterSearch;
    if (filterUserId) filterPayload.userId = filterUserId;
    if (filterCreatedAt) filterPayload.createdAt = filterCreatedAt;
    if (filterCatId) filterPayload.catId = filterCatId;
    if (filterPriority) filterPayload.isHighPriority = filterPriority;
    if (filterStatus) filterPayload.type = filterStatus;
    try {
      const res: any = await POST(endpoints.filter, filterPayload);
      setData(res.data || []);
      if (contentType === "news") {
        const meta: any = await POST(API.GET_NEWS_META, filterPayload);
        setTotalCount(meta.totalCount || 0);
        setPublishedCount(meta.publishedCount || 0);
        setTrashedCount(meta.trashedCount || 0);
        setDraftCount(meta.draftCount || 0);
        setScheduledCount(meta.scheduledCount || 0);
      } else {
        setTotalCount(res.totalCount || 0);
        setPublishedCount(res.publishedCount || 0);
        setTrashedCount(res.trashedCount || 0);
        setDraftCount(res.draftCount || 0);
        setScheduledCount(res.scheduledCount || 0);
      }
      setLoading(false);
      setIndicator(false);
    } catch (error) {
      console.error("Error loading data:", error);
      message.error("Failed to load data");
      setData([]);
      setLoading(false);
      setIndicator(false);
    }
  };

  const loadGlobalSearch = useCallback(async (searchText: string) => {
    if (searchText.length < 3) {
      setGlobalSearchResults({
        news: { data: [], totalCount: 0 },
        article: { data: [], totalCount: 0 },
        editorial: { data: [], totalCount: 0 },
        vidya: { data: [], totalCount: 0 },
        njayar: { data: [], totalCount: 0 },
      });
      return;
    }
    setGlobalSearchLoading(true);
    try {
      const promises = ALL_CONTENT_TYPES.map(async (ct) => {
        const endpoints = API_ENDPOINTS[ct];
        const payload = { start: 0, limit: 10, search: searchText };
        try {
          const res: any = await POST(endpoints.filter, payload);
          let total = res.totalCount || 0;
          if (ct === "news") {
            const meta: any = await POST(API.GET_NEWS_META, payload);
            total = meta.totalCount || 0;
          }
          return { type: ct, data: res.data || [], totalCount: total };
        } catch {
          return { type: ct, data: [], totalCount: 0 };
        }
      });
      const results = await Promise.all(promises);
      const grouped: any = {};
      results.forEach((r) => {
        grouped[r.type] = { data: r.data, totalCount: r.totalCount };
      });
      setGlobalSearchResults(grouped);
    } catch (error) {
      console.error("Global search error:", error);
    } finally {
      setGlobalSearchLoading(false);
    }
  }, []);

  const loadUsers = async () => {
    try {
      const url = API.BASE_URL + API.GET_USERS;
      const response = await axios.get(url, { withCredentials: true });
      setUsersList(response.data || []);
    } catch { /* ignore */ }
  };

  const loadCategoryList = async () => {
    try {
      const res: any = await LoadCategories();
      setCategoryList(res?.data || []);
    } catch { /* ignore */ }
  };

  // =================== Effects ===================

  useEffect(() => {
    if (!globalSearch) loadData(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, page, pageSize, filterSearch, filterUserId, filterCreatedAt, filterCatId, filterPriority, filterStatus, globalSearch]);

  useEffect(() => {
    loadUsers();
    loadCategoryList();
  }, []);

  useEffect(() => {
    if (urlParams.globalSearch && urlParams.globalSearch.length >= 3) {
      loadGlobalSearch(urlParams.globalSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =================== Filter Handlers ===================

  const onFilterSearchChange = (value: string) => {
    setFilterSearch(value);
    setPage(1);
    syncUrl({ search: value, page: 1 });
  };

  const onUserFilter = (userId: any) => {
    setFilterUserId(userId || "");
    setPage(1);
    syncUrl({ userId: userId || "", page: 1 });
  };

  const onChangeDate = (_date: any, dateString: any) => {
    setFilterCreatedAt(dateString || "");
    setPage(1);
    syncUrl({ createdAt: dateString || "", page: 1 });
  };

  const onCategoryFilter = (catId: any) => {
    setFilterCatId(catId || "");
    setPage(1);
    syncUrl({ catId: catId || "", page: 1 });
  };

  const onPriorityFilter = (priority: any) => {
    setFilterPriority(priority || "");
    setPage(1);
    syncUrl({ isHighPriority: priority || "", page: 1 });
  };

  const onStatusFilter = (type: any) => {
    setFilterStatus(type || "");
    setPage(1);
    syncUrl({ type: type || "", page: 1 });
  };

  const clearFilter = (key: string) => {
    const setterMap: Record<string, (v: any) => void> = {
      search: setFilterSearch,
      userId: setFilterUserId,
      createdAt: setFilterCreatedAt,
      catId: setFilterCatId,
      isHighPriority: setFilterPriority,
      type: setFilterStatus,
    };
    if (setterMap[key]) setterMap[key]("");
    setPage(1);
    syncUrl({ [key]: "", page: 1 });
  };

  const onPageChange = (newPage: number, newPageSize: number) => {
    window.scrollTo(0, 0);
    setPage(newPage);
    setPageSize(newPageSize);
    syncUrl({ page: newPage, limit: newPageSize });
    loadData(newPage, newPageSize);
  };

  const onTabChange = (key: string) => {
    const newTab = key as ContentType;
    setContentType(newTab);
    setPage(1);
    setFilterSearch("");
    setFilterUserId("");
    setFilterCreatedAt("");
    setFilterCatId("");
    setFilterPriority("");
    setFilterStatus("");
    const params: Record<string, string | number> = { tab: newTab };
    if (globalSearch) params.globalSearch = globalSearch;
    navigate({ search: buildUrlString(params) }, { replace: true });
  };

  // =================== Global Search ===================

  const onGlobalSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalSearch(value);
    if (globalSearchTimeout.current) clearTimeout(globalSearchTimeout.current);
    if (value.length === 0) {
      setGlobalSearchResults({
        news: { data: [], totalCount: 0 },
        article: { data: [], totalCount: 0 },
        editorial: { data: [], totalCount: 0 },
        vidya: { data: [], totalCount: 0 },
        njayar: { data: [], totalCount: 0 },
      });
      syncUrl({ globalSearch: "" });
      loadData();
      return;
    }
    syncUrl({ globalSearch: value });
    if (value.length >= 3) {
      globalSearchTimeout.current = setTimeout(() => loadGlobalSearch(value), 500);
    }
  };

  const clearGlobalSearch = () => {
    setGlobalSearch("");
    setGlobalSearchResults({
      news: { data: [], totalCount: 0 },
      article: { data: [], totalCount: 0 },
      editorial: { data: [], totalCount: 0 },
      vidya: { data: [], totalCount: 0 },
      njayar: { data: [], totalCount: 0 },
    });
    syncUrl({ globalSearch: "" });
    loadData();
  };

  // =================== Actions ===================

  const onEdit = (record: any, ct?: ContentType) => {
    const type = ct || contentType;
    if (type === "njayar") {
      navigate(`/Auth/unified-content/njayar/master/${record.id}`, {
        state: { items: record },
      });
    } else {
      navigate(`/Auth/unified-content/edit/${type}/${record.id}`);
    }
  };

  const onTrash = async (record: any, ct?: ContentType) => {
    const type = ct || contentType;
    try {
      if (type === "news") {
        await POST(API.ADD_TO_TRASH, { id: record.id, userId: User?.id });
      } else {
        const ep: Record<string, string> = { article: API.ARTICLE, editorial: API.EDITORIALS, vidya: API.VIDYA_PRABHAADHAM, njayar: API.NJAYAR_MASTER };
        await PUT(ep[type] + "/" + record.id, { data: { is_deleted: true } });
      }
      message.success("Moved to trash");
      if (globalSearch) loadGlobalSearch(globalSearch);
      else loadData();
    } catch { message.error("Failed to trash"); }
  };

  const onUnTrash = async (record: any, ct?: ContentType) => {
    const type = ct || contentType;
    try {
      if (type === "news") {
        await axios.put(API.BASE_URL + API.UNTRASH + `/${record.id}`);
      } else {
        const ep: Record<string, string> = { article: API.ARTICLE, editorial: API.EDITORIALS, vidya: API.VIDYA_PRABHAADHAM, njayar: API.NJAYAR_MASTER };
        await PUT(ep[type] + "/" + record.id, { data: { is_deleted: false } });
      }
      message.success("Recovered from trash");
      if (globalSearch) loadGlobalSearch(globalSearch);
      else loadData();
    } catch { message.error("Failed to recover"); }
  };

  const onDeleteItem = async (id: number, ct?: ContentType) => {
    const type = ct || contentType;
    try {
      const ep: Record<string, string> = { news: API.ALL_NEWS, article: API.ARTICLE, editorial: API.EDITORIALS, vidya: API.VIDYA_PRABHAADHAM, njayar: API.NJAYAR_MASTER };
      await axios.delete(API.BASE_URL + ep[type] + `/${id}`);
      message.success("Deleted");
      if (globalSearch) loadGlobalSearch(globalSearch);
      else loadData();
    } catch { message.error("Failed to delete"); }
  };

  const generateLink = (record: any, linkType: "copy" | "preview", ct?: ContentType) => {
    const type = ct || contentType;
    const attrs = record?.attributes || record;
    const perma = (attrs?.permalink || "")?.replace(/\s+/g, "-");
    const link = URL_PATTERNS[type](record.id, perma);
    if (linkType === "copy") {
      navigator.clipboard.writeText(link).then(() => message.success("Link copied"));
    } else {
      window.open(link, "_blank");
    }
  };

  // =================== Field Accessors ===================

  const getAttr = (record: any) => record?.attributes || record;

  const getImage = (record: any, ct?: ContentType) => {
    const type = ct || contentType;
    const a = getAttr(record);
    if (type === "news") return a?.image;
    if (type === "njayar") return a?.thumbnail || a?.image1;
    return a?.image1;
  };

  const getHeading = (record: any, ct?: ContentType) => {
    const type = ct || contentType;
    const a = getAttr(record);
    if (type === "njayar") return a?.head || a?.title || "";
    if (type === "vidya") return a?.title || "";
    return a?.head || "";
  };

  const getCreatedBy = (record: any) => getAttr(record)?.user?.username || "";

  const getTypeTags = (record: any, ct?: ContentType) => {
    const type = ct || contentType;
    const a = getAttr(record);
    if (type === "news") return a?.categories?.map((c: any) => c?.category).filter(Boolean) || [];
    if (type === "article") return a?.articleTag?.split("|").filter(Boolean) || [];
    if (type === "editorial") return a?.EditorialTag?.split("|").filter(Boolean) || [];
    if (type === "njayar") return a?.tags?.map((t: any) => t?.tag || t).filter(Boolean) || a?.NjayarTags?.split("|").filter(Boolean) || [];
    return [];
  };

  // =================== Table Columns ===================

  const getColumns = (ct?: ContentType): ColumnsType<any> => {
    const type = ct || contentType;
    return [
      {
        title: "#",
        key: "id",
        width: 46,
        render: (_: any, __: any, i: number) => (
          <span className="uc-table-cell-muted">
            {ct ? i + 1 : (page - 1) * pageSize + i + 1}
          </span>
        ),
      },
      {
        title: "Image",
        key: "photo",
        width: 80,
        render: (_: any, record: any) => {
          const img = getImage(record, type);
          return img ? (
            <Image
              src={img}
              className="uc-table-thumb"
              alt=""
              fallback={s3FallbackUrl(img) || undefined}
            />
          ) : (
            <div className="uc-table-noimg">No image</div>
          );
        },
      },
      {
        title: "Headline",
        key: "headline",
        ellipsis: true,
        width: 300,
        render: (_: any, record: any) => {
          const a = getAttr(record);
          return (
            <div>
              {type === "news" && a?.onlive && (
                <Tag color="red" style={{ fontSize: 10, marginBottom: 2 }}>LIVE</Tag>
              )}
              <div className="uc-table-headline" onClick={() => onEdit(record, type)}>
                {getHeading(record, type)}
              </div>
            </div>
          );
        },
      },
      {
        title: "Author",
        key: "createdBy",
        width: 120,
        render: (_: any, record: any) => (
          <span className="uc-table-cell-sm">{getCreatedBy(record)}</span>
        ),
      },
      {
        title: "Created",
        key: "createdAt",
        width: 150,
        render: (_: any, record: any) => {
          const a = getAttr(record);
          return (
            <span className="uc-table-cell-sm">
              {a?.createdAt ? moment(a.createdAt).format("DD MMM YYYY, h:mm A") : "-"}
            </span>
          );
        },
      },
      ...(type === "news" ? [{
        title: "Published",
        key: "publishedAt",
        width: 150,
        render: (_: any, record: any) => {
          const a = getAttr(record);
          return a?.publishedAt ? (
            <span className="uc-table-cell-sm">
              {moment(a.publishedAt).format("DD MMM YYYY, h:mm A")}
            </span>
          ) : (
            <span className="uc-table-cell-muted">--</span>
          );
        },
      }] : []),
      {
        title: "Tags",
        key: "type",
        width: 170,
        render: (_: any, record: any) => {
          const tags = getTypeTags(record, type);
          return (
            <div>
              {tags.slice(0, 2).map((tag: string, i: number) => (
                <Tag key={i} style={{ fontSize: 10, marginBottom: 2 }}>{tag}</Tag>
              ))}
              {tags.length > 2 && (
                <Tag style={{ fontSize: 10, marginBottom: 2 }}>+{tags.length - 2}</Tag>
              )}
            </div>
          );
        },
      },
      {
        title: "",
        key: "priority",
        width: 28,
        render: (_: any, record: any) => {
          const a = getAttr(record);
          if (a?.isHighPriority) return <TbSquareNumber1 size={16} color="#0055a6" />;
          if (a?.isSecondLeads || a?.isSecondPriority) return <TbSquareNumber2 size={16} color="#888" />;
          if (a?.isThirdPriority) return <TbSquareNumber3 size={16} color="#aaa" />;
          return null;
        },
      },
      {
        title: "Status",
        key: "status",
        width: 110,
        render: (_: any, record: any) => {
          const a = getAttr(record);
          if (a?.is_deleted) return <Tag className="uc-status-trash">Trash</Tag>;
          if (a?.publishedAt) return <Tag className="uc-status-published">Published</Tag>;
          if (a?.scheduled_at && !a?.publishedAt)
            return (
              <Tooltip title={moment(a.scheduled_at).format("DD MMM YYYY, h:mm A")}>
                <Tag className="uc-status-scheduled">Scheduled</Tag>
              </Tooltip>
            );
          return <Tag className="uc-status-draft">Draft</Tag>;
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: 140,
        render: (_: any, record: any) => {
          const a = getAttr(record);
          return (
            <div className="uc-table-actions">
              {type === "news" && (
                <Tooltip title="Log">
                  <FaRegEye color="#666" size={17} onClick={() => { setLogItemId(record.id); setLogShow(true); }} />
                </Tooltip>
              )}
              {a?.publishedAt && (
                <>
                  <Tooltip title="Preview">
                    <IoMdOpen color="#666" size={17} onClick={() => generateLink(record, "preview", type)} />
                  </Tooltip>
                  <Tooltip title="Copy link">
                    <IoCopyOutline color="#52c41a" size={17} onClick={() => generateLink(record, "copy", type)} />
                  </Tooltip>
                </>
              )}
              <Tooltip title="Edit">
                <BiEdit color="#faad14" size={17} onClick={() => onEdit(record, type)} />
              </Tooltip>
              <Tooltip title={a?.is_deleted ? "Recover" : "Trash"}>
                {a?.is_deleted ? (
                  <LuUndo2 color="#0055a6" size={17} onClick={() => onUnTrash(record, type)} />
                ) : (
                  <IoTrashOutline color="#ff4d4f" size={17} onClick={() => onTrash(record, type)} />
                )}
              </Tooltip>
              {a?.is_deleted && User?.role?.id === 4 && (
                <Popconfirm
                  title="Permanently delete?"
                  description="This action cannot be undone."
                  onConfirm={() => onDeleteItem(record.id, type)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button size="small" danger style={{ fontSize: 10, padding: "0 6px", height: 22 }}>
                    Delete
                  </Button>
                </Popconfirm>
              )}
            </div>
          );
        },
      },
    ];
  };

  // =================== Computed ===================

  const activeFilters: { key: string; label: string; value: string }[] = [];
  if (filterSearch) activeFilters.push({ key: "search", label: "Search", value: filterSearch });
  if (filterUserId) {
    const user = usersList.find((u) => String(u.id) === String(filterUserId));
    activeFilters.push({ key: "userId", label: "Author", value: user?.username || filterUserId });
  }
  if (filterCreatedAt) activeFilters.push({ key: "createdAt", label: "Date", value: moment(filterCreatedAt).format("DD MMM YYYY") });
  if (filterCatId) {
    const cat = categoryList.find((c) => String(c.id) === String(filterCatId));
    activeFilters.push({ key: "catId", label: "Category", value: cat?.attributes?.category || filterCatId });
  }
  if (filterPriority) activeFilters.push({ key: "isHighPriority", label: "Priority", value: filterPriority === "high" ? "High" : "Second" });
  if (filterStatus) activeFilters.push({ key: "type", label: "Status", value: filterStatus });

  const tabItems = (Object.keys(CONTENT_TYPE_LABELS) as ContentType[]).map((key) => ({
    key,
    label: CONTENT_TYPE_LABELS[key],
  }));

  const isGlobalSearchActive = globalSearch.length >= 3;
  const totalGlobalResults = ALL_CONTENT_TYPES.reduce((sum, ct) => sum + (globalSearchResults[ct]?.totalCount || 0), 0);
  const sectionsWithResults = ALL_CONTENT_TYPES.filter((ct) => (globalSearchResults[ct]?.totalCount || 0) > 0);

  const allCount = Number(publishedCount) + Number(scheduledCount) + Number(trashedCount) + Number(draftCount);

  // =================== Render ===================

  return (
    <div className="uc-page">
      {/* ---- Top Bar: Title | Search | Add ---- */}
      <div className="uc-topbar">
        <div className="uc-topbar-row">
          <div style={{ minWidth: 120 }}>
            <div className="uc-topbar-title">Content</div>
            {!isGlobalSearchActive && (
              <div className="uc-topbar-subtitle">
                {totalCount} {CONTENT_TYPE_LABELS[contentType]}
              </div>
            )}
          </div>
          <div className="uc-topbar-search-wrap">
            <Input
              size="large"
              placeholder="Search across News, Articles, Editorials, Vidya & Njayar..."
              prefix={<SearchOutlined style={{ color: "#999", fontSize: 18 }} />}
              value={globalSearch}
              onChange={onGlobalSearchChange}
              allowClear
              className="uc-master-search-input"
            />
          </div>
          <div className="uc-topbar-actions">
            <Button
              size="large"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => contentType === "njayar" ? navigate(`/Auth/unified-content/njayar/create-master`) : navigate(`/Auth/unified-content/create?type=${contentType}`)}
            >
              New Entry
            </Button>
          </div>
        </div>
        {isGlobalSearchActive && !globalSearchLoading && (
          <div className="uc-search-meta" style={{ textAlign: "center", paddingBottom: 8 }}>
            {totalGlobalResults} result{totalGlobalResults !== 1 ? "s" : ""} across {sectionsWithResults.length} section{sectionsWithResults.length !== 1 ? "s" : ""}
          </div>
        )}
        {isGlobalSearchActive && globalSearchLoading && (
          <div className="uc-search-meta" style={{ textAlign: "center", paddingBottom: 8 }}><Spin size="small" /> Searching...</div>
        )}
      </div>
      {indicator && <LinearProgress color="primary" style={{ height: 2 }} />}

      {/* ---- Tab View (when not in global search) ---- */}
      {!isGlobalSearchActive && (
        <>
          {/* Filters toolbar */}
          <div className="uc-toolbar">
            <Input
              allowClear
              placeholder={`Filter ${CONTENT_TYPE_LABELS[contentType]}...`}
              prefix={<SearchOutlined style={{ color: "#bbb" }} />}
              value={filterSearch}
              onChange={(e: any) => onFilterSearchChange(e.target.value)}
              style={{ maxWidth: 200 }}
            />
            <Select
              placeholder="Author"
              value={filterUserId || undefined}
              onChange={(val) => onUserFilter(val)}
              showSearch
              style={{ width: 160 }}
              allowClear
              onClear={() => clearFilter("userId")}
              filterOption={(input: any, option: any) =>
                option?.children?.toLowerCase()?.indexOf(input.toLowerCase()) >= 0
              }
            >
              {usersList?.map((item: any, i: number) => (
                <Select.Option key={i} value={item.id}>{item.username}</Select.Option>
              ))}
            </Select>
            <DatePicker
              allowClear
              style={{ width: 140 }}
              placeholder="Date"
              value={filterCreatedAt ? dayjs(filterCreatedAt) : null}
              onChange={onChangeDate}
            />
            {contentType === "news" && (
              <>
                <Select
                  showSearch
                  placeholder="Category"
                  value={filterCatId || undefined}
                  onChange={(val) => onCategoryFilter(val)}
                  style={{ width: 150 }}
                  allowClear
                  onClear={() => clearFilter("catId")}
                  filterOption={(input: any, option: any) =>
                    option?.children?.toLowerCase()?.indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {categoryList?.map((item: any, i: number) => (
                    <Select.Option key={i} value={item.id}>{item?.attributes?.category}</Select.Option>
                  ))}
                </Select>
                <Select
                  placeholder="Priority"
                  value={filterPriority || undefined}
                  onChange={(val) => onPriorityFilter(val)}
                  style={{ width: 130 }}
                  allowClear
                  onClear={() => clearFilter("isHighPriority")}
                >
                  <Select.Option value="high">High Priority</Select.Option>
                  <Select.Option value="second">Second Lead</Select.Option>
                </Select>
              </>
            )}
            <div style={{ flex: 1 }} />
            <Button icon={<ReloadOutlined />} onClick={() => loadData()}>Refresh</Button>
          </div>

          {/* Status bar */}
          <div className="uc-statusbar">
            <div className="uc-statusbar-filters">
              {activeFilters.length > 0 && (
                <>
                  <span style={{ color: "#888" }}>Filters:</span>
                  {activeFilters.map((f) => (
                    <Tag key={f.key} closable onClose={() => clearFilter(f.key)} style={{ fontSize: 11 }}>
                      {f.label}: {f.value}
                    </Tag>
                  ))}
                </>
              )}
            </div>
            <div className="uc-statusbar-spacer" />
            <Radio.Group
              value={filterStatus || "All"}
              onChange={(e: any) => onStatusFilter(e.target.value === "All" ? "" : e.target.value)}
              size="middle"
              buttonStyle="solid"
            >
              <Radio.Button value="All">All: {loading ? "" : allCount}</Radio.Button>
              <Radio.Button value="published">Published: {loading ? "" : publishedCount}</Radio.Button>
              {(contentType === "news" || contentType === "vidya") && (
                <Radio.Button value="scheduled">Scheduled: {loading ? "" : scheduledCount}</Radio.Button>
              )}
              <Radio.Button value="trash">Trash: {loading ? "" : trashedCount}</Radio.Button>
              <Radio.Button value="draft">Draft: {loading ? "" : draftCount}</Radio.Button>
            </Radio.Group>
          </div>

          {/* Tabs */}
          <div className="uc-tabs-bar">
            <Tabs 
              size="middle"
              activeKey={contentType}
              onChange={onTabChange}
              items={tabItems}
              className="uc-tabs"
            />
          </div>

          {/* Table + Pagination */}
          <div className="uc-content">
            <Table
              className="uc-table"
              columns={getColumns()}
              dataSource={data}
              rowKey={(record) => record.id}
              pagination={false}
              size="small"
              loading={loading}
              scroll={{ x: 1000 }}
            />
            <div className="uc-pagination">
              <Pagination
                responsive
                current={page}
                total={totalCount}
                pageSize={pageSize}
                showSizeChanger
                showTotal={(total) => `${total} items`}
                onChange={(p, ps) => onPageChange(p, ps)}
              />
            </div>
          </div>
        </>
      )}

      {/* ---- Global Search Results ---- */}
      {isGlobalSearchActive && (
        <div className="uc-global-results">
          {globalSearchLoading ? (
            <div style={{ textAlign: "center", padding: 80 }}>
              <Spin size="large" />
              <div style={{ marginTop: 12, color: "#888" }}>Searching all sections...</div>
            </div>
          ) : totalGlobalResults === 0 ? (
            <div className="uc-global-empty">
              No results found for "<strong>{globalSearch}</strong>"
            </div>
          ) : (
            sectionsWithResults.map((ct) => (
              <div key={ct} className="uc-global-section">
                <div className="uc-global-section-header">
                  <span className="uc-global-section-title">{CONTENT_TYPE_LABELS[ct]}</span>
                  <Tag color="blue" style={{ fontSize: 11 }}>{globalSearchResults[ct].totalCount}</Tag>
                  <div style={{ flex: 1 }} />
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      setGlobalSearch("");
                      setContentType(ct);
                      setFilterSearch(globalSearch);
                      setPage(1);
                      navigate({ search: buildUrlString({ tab: ct, search: globalSearch }) }, { replace: true });
                    }}
                  >
                    View all &rarr;
                  </Button>
                </div>
                <Table
                  className="uc-table"
                  columns={getColumns(ct)}
                  dataSource={globalSearchResults[ct].data}
                  rowKey={(record) => `${ct}-${record.id}`}
                  pagination={false}
                  size="small"
                  scroll={{ x: 1000 }}
                />
              </div>
            ))
          )}
        </div>
      )}

      {logShow && (
        <NewsLog
          visible={logShow}
          id={logItemId}
          close={() => setLogShow(false)}
        />
      )}
    </div>
  );
}

export default UnifiedContent;
